const { getElements, getOffsetTop } = window.BAO.Utils

class CollectionScroll extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.isTicking = false
    this.lastKnownY = window.scrollY

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    this.cachedScrollPositions = null
    this.isVisible = false
    this.lastScrollAmount = null

    this.setupIntersectionObserver()
    this.updateEl()
  }

  setupIntersectionObserver () {
    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting
        if (!entry.isIntersecting) {
          this.isTicking = false
        } else if (entry.isIntersecting) {
          this.requestTick()
        }
      },
      { rootMargin: '200px' },
    )
    this.observer.observe(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    this.debouncedOnResize = window.BAO.Utils.debounce(() => {
      this.cachedScrollPositions = null
    }, 200)

    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.debouncedOnResize)

    this.onProductGridChanged = this.onProductGridChanged.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT_GRID.RENDERED,
      this.onProductGridChanged,
    )

    this.onViewportChange = this.onViewportChange.bind(this)
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.VIEWPORT.CHANGE,
      this.onViewportChange,
    )
  }

  onScroll () {
    if (!this.isVisible) return

    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.moveGrid()
  }

  moveGrid () {
    if (!window.BAO.MediaBreakpoints.isSm) return

    const navWidthPercent = (190 / window.innerWidth) * 100
    const scrollerViewportWidth = 100 - navWidthPercent

    const heroOffset = this.dataset.hasHero === 'true' ? 380 : 0

    const maxScrollAmount =
      this.totalCardSize + this.totalPromoSize - scrollerViewportWidth

    if (!this.cachedScrollPositions) {
      const elementHeight = this.offsetHeight
      const offsetTop = getOffsetTop(this)
      this.cachedScrollPositions = {
        start: offsetTop + heroOffset,
        end: offsetTop + elementHeight - window.innerHeight,
      }
    }
    const elementStartPoint = this.cachedScrollPositions.start
    const elementEndPoint = this.cachedScrollPositions.end

    if (this.lastKnownY <= elementStartPoint) {
      this.style.setProperty('--Scroll_Amount', `0vw`)

      return
    }

    if (this.lastKnownY > elementEndPoint) {
      this.style.setProperty('--Scroll_Amount', `-${maxScrollAmount}vw`)

      return
    }

    const totalScrollAmount = elementEndPoint - elementStartPoint

    const scrollYAsAPercentageOfJourney =
      (this.lastKnownY - elementStartPoint) / totalScrollAmount

    const scrollAmount = maxScrollAmount * scrollYAsAPercentageOfJourney

    this.style.setProperty('--Scroll_Amount', `-${scrollAmount}vw`)
  }

  onProductGridChanged () {
    this.cachedScrollPositions = null

    const productEls = getElements(this.els.product?.selector)
    const promoEls = getElements(this.els.promo?.selector)
    const paginationEls = getElements(this.els.pagination?.selector)

    const productWidthCalc = productEls.length * this.singleCardSize
    const promoWidthCalc = promoEls.length * this.singlePromoSize
    const paginationWidthCalc = paginationEls.length * this.singleCardSize

    const newTotalCards = productWidthCalc + paginationWidthCalc
    const newTotalPromos = promoWidthCalc

    const newTotalHeight =
      productWidthCalc + promoWidthCalc + paginationWidthCalc

    this.setAttribute('data-total-card-size', newTotalCards)
    this.setAttribute('data-total-promo-size', newTotalPromos)
    this.style.setProperty('--Card_Total', `${newTotalHeight}vh`)
  }

  onViewportChange () {
    this.cachedScrollPositions = null

    this.updateEl()
  }

  get singleCardSize () {
    if (window.BAO.MediaBreakpoints.isSmViewport) {
      return parseInt(this.dataset.cardSizeTablet)
    } else if (
      window.BAO.MediaBreakpoints.isMdViewport ||
      window.BAO.MediaBreakpoints.isLgViewport
    ) {
      return parseInt(this.dataset.cardSizeDesktop)
    } else {
      return parseInt(this.dataset.cardSizeLargeDesktop)
    }
  }

  get totalCardSize () {
    if (window.BAO.MediaBreakpoints.isSmViewport) {
      return parseInt(this.dataset.totalCardSizeTablet)
    } else if (
      window.BAO.MediaBreakpoints.isMdViewport ||
      window.BAO.MediaBreakpoints.isLgViewport
    ) {
      return parseInt(this.dataset.totalCardSizeDesktop)
    } else {
      return parseInt(this.dataset.totalCardSizeLargeDesktop)
    }
  }

  get singlePromoSize () {
    return parseInt(this.dataset.promoSize)
  }

  get totalPromoSize () {
    return parseInt(this.dataset.totalPromoSize)
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.debouncedOnResize)

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT_GRID.RENDERED,
      this.onProductGridChanged,
    )

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.VIEWPORT.CHANGE,
      this.onViewportChange,
    )

    this.cachedScrollPositions = null
  }
}

if (!customElements.get('collection-scroll')) {
  customElements.define('collection-scroll', CollectionScroll)
}
